/**
 * Design tokens curados do Santinho Digital.
 *
 * Mantenha em sincronia com .claude/skills/santinho-design-system.md e
 * docs/design/santinho-moodboard.md. Cada paleta passa contraste WCAG AA
 * (primary vs branco >= 4.5:1).
 */

import type { ColorPaletteId, TypographyPresetId } from '@/types/tenant.types';

// =============================================================================
// Color Palettes
// =============================================================================

export interface ColorPalette {
  id: ColorPaletteId;
  name: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

export const COLOR_PALETTES: ColorPalette[] = [
  {
    id: 'institutional-blue',
    name: 'Institucional Azul',
    description: 'Tradicional, confiável. Senador, deputado federal',
    primaryColor: '#1E3A8A',
    secondaryColor: '#3B82F6',
    accentColor: '#DBEAFE',
  },
  {
    id: 'popular-red',
    name: 'Popular Vermelho',
    description: 'Esquerda clássica, base sindical',
    primaryColor: '#B91C1C',
    secondaryColor: '#DC2626',
    accentColor: '#FEE2E2',
  },
  {
    id: 'progressive-purple',
    name: 'Progressista Roxo',
    description: 'Esquerda moderna, pauta jovem',
    primaryColor: '#6D28D9',
    secondaryColor: '#8B5CF6',
    accentColor: '#EDE9FE',
  },
  {
    id: 'sustainable-green',
    name: 'Verde Sustentável',
    description: 'Pauta ambiental, agronegócio',
    primaryColor: '#15803D',
    secondaryColor: '#22C55E',
    accentColor: '#DCFCE7',
  },
  {
    id: 'center-teal',
    name: 'Centro Equilíbrio',
    description: 'Neutro técnico, perfil moderado',
    primaryColor: '#0F766E',
    secondaryColor: '#14B8A6',
    accentColor: '#CCFBF1',
  },
  {
    id: 'conservative-navy',
    name: 'Conservador Marinho',
    description: 'Direita institucional',
    primaryColor: '#1E293B',
    secondaryColor: '#475569',
    accentColor: '#E2E8F0',
  },
  {
    id: 'liberal-yellow',
    name: 'Liberal Amarelo',
    description: 'Direita liberal, empreendedor',
    primaryColor: '#CA8A04',
    secondaryColor: '#EAB308',
    accentColor: '#FEF9C3',
  },
  {
    id: 'youth-pink',
    name: 'Jovem Energia',
    description: 'Primeiro mandato, base jovem urbana',
    primaryColor: '#DB2777',
    secondaryColor: '#EC4899',
    accentColor: '#FCE7F3',
  },
];

export function getPalette(id?: ColorPaletteId): ColorPalette | undefined {
  if (!id || id === 'custom') return undefined;
  return COLOR_PALETTES.find((p) => p.id === id);
}

// =============================================================================
// Typography Presets
// =============================================================================

export interface TypographyPreset {
  id: TypographyPresetId;
  name: string;
  description: string;
  fontDisplay: string;
  fontBody: string;
  /** URLs de Google Fonts (carregadas via @import no documento publico). */
  fontUrls?: string[];
}

export const TYPOGRAPHY_PRESETS: TypographyPreset[] = [
  {
    id: 'institutional',
    name: 'Institucional',
    description: 'Playfair Display + Source Sans Pro',
    fontDisplay: '"Playfair Display", Georgia, serif',
    fontBody: '"Source Sans Pro", -apple-system, sans-serif',
    fontUrls: [
      'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;800&family=Source+Sans+Pro:wght@400;600;700&display=swap',
    ],
  },
  {
    id: 'modern',
    name: 'Moderno',
    description: 'Inter (display + body)',
    fontDisplay: '"Inter", -apple-system, sans-serif',
    fontBody: '"Inter", -apple-system, sans-serif',
    fontUrls: [
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap',
    ],
  },
  {
    id: 'popular',
    name: 'Popular',
    description: 'Oswald + Open Sans',
    fontDisplay: '"Oswald", Impact, sans-serif',
    fontBody: '"Open Sans", -apple-system, sans-serif',
    fontUrls: [
      'https://fonts.googleapis.com/css2?family=Oswald:wght@500;700&family=Open+Sans:wght@400;600;700&display=swap',
    ],
  },
  {
    id: 'editorial',
    name: 'Editorial',
    description: 'Merriweather + Lato',
    fontDisplay: '"Merriweather", Georgia, serif',
    fontBody: '"Lato", -apple-system, sans-serif',
    fontUrls: [
      'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700;900&family=Lato:wght@400;700&display=swap',
    ],
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Montserrat (display + body)',
    fontDisplay: '"Montserrat", -apple-system, sans-serif',
    fontBody: '"Montserrat", -apple-system, sans-serif',
    fontUrls: [
      'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;800;900&display=swap',
    ],
  },
  {
    id: 'classic',
    name: 'Clássico',
    description: 'Raleway + PT Sans',
    fontDisplay: '"Raleway", -apple-system, sans-serif',
    fontBody: '"PT Sans", -apple-system, sans-serif',
    fontUrls: [
      'https://fonts.googleapis.com/css2?family=Raleway:wght@500;700;800&family=PT+Sans:wght@400;700&display=swap',
    ],
  },
];

export function getTypographyPreset(id?: TypographyPresetId): TypographyPreset | undefined {
  if (!id || id === 'custom') return undefined;
  return TYPOGRAPHY_PRESETS.find((p) => p.id === id);
}

// =============================================================================
// Default
// =============================================================================

export const DEFAULT_PALETTE_ID: ColorPaletteId = 'institutional-blue';
export const DEFAULT_TYPOGRAPHY_ID: TypographyPresetId = 'modern';
